import { useState, useCallback, useRef, useMemo } from 'react'
import {
  ChevronRight,
  Search,
  Lightbulb,
  BookOpen,
  Loader2,
  HelpCircle,
} from 'lucide-react'
import { useLocation } from 'react-router-dom'
import { useUser } from '../../hooks/useUser'
import { searchGuide, type GuideSearchResult } from '../../lib/api'
import { troubleshootingItems } from '../../lib/troubleshooting-data'

type SearchResult =
  | { type: 'guide'; slug: string; title: string; excerpt: string }
  | { type: 'troubleshooting'; id: string; title: string; excerpt: string }

interface WidgetHomeTabProps {
  onSwitchToHelp: (section?: string, anchor?: string) => void
  onStartFeedback: () => void
}

interface QuestionLink {
  question: string
  section: string
  anchor?: string
}

const SLACK_URL = 'https://exante.slack.com/archives/C0ACURB6A9X'

/** Context-aware question links mapped by route prefix */
const CONTEXTUAL_QUESTIONS: Record<string, QuestionLink[]> = {
  '/apps/:detail': [
    { question: 'How do I configure app settings?', section: 'apps', anchor: 'app-settings' },
    { question: 'How do I deploy this app?', section: 'deployment', anchor: 'dev-preview' },
    { question: 'How do I share this app?', section: 'sharing', anchor: 'sharing-an-app' },
    { question: 'How do I check build logs?', section: 'deployment', anchor: 'build-logs' },
    { question: 'How do I add app secrets?', section: 'deployment', anchor: 'app-secrets' },
    { question: 'How do I set up credential injection?', section: 'deployment', anchor: 'credential-injection' },
    { question: 'How do I use feature branches?', section: 'deployment', anchor: 'feature-branches' },
  ],
  '/apps': [
    { question: 'How do I create a new app?', section: 'apps', anchor: 'creating-an-app' },
    { question: 'How do I configure app settings?', section: 'apps', anchor: 'app-settings' },
    { question: 'How do I share my app with others?', section: 'sharing', anchor: 'sharing-an-app' },
    { question: 'How do I deploy to production?', section: 'deployment', anchor: 'production-builds' },
    { question: 'How do I delete or restore an app?', section: 'apps', anchor: 'deleting-and-restoring-apps' },
    { question: 'How do I check build logs?', section: 'deployment', anchor: 'build-logs' },
  ],
  '/browse': [
    { question: 'How do I discover apps?', section: 'browse', anchor: 'browsing-apps' },
    { question: 'How do I get my app on Browse?', section: 'browse', anchor: 'getting-your-app-on-browse' },
    { question: 'How do I request access to an app?', section: 'browse', anchor: 'requesting-access' },
    { question: 'What are visitor metrics?', section: 'browse', anchor: 'visitor-metrics' },
    { question: 'What are featured apps?', section: 'browse', anchor: 'featured-apps' },
    { question: 'How do I favorite an app?', section: 'browse', anchor: 'favorites' },
  ],
  '/workspace': [
    { question: 'How do I use the file viewer?', section: 'workspaces', anchor: 'file-viewer' },
    { question: 'How do I use the terminal?', section: 'terminal' },
    { question: 'How do I import a project?', section: 'import' },
    { question: 'How do I use the AI agent?', section: 'workspaces', anchor: 'ai-agent' },
    { question: 'How do I manage sessions?', section: 'workspaces', anchor: 'session-management' },
    { question: 'How do I use multiple terminal tabs?', section: 'terminal', anchor: 'multiple-tabs' },
    { question: 'How do I customize the terminal?', section: 'terminal', anchor: 'customization' },
  ],
  '/account': [
    { question: 'How do I get started with the platform?', section: 'getting-started' },
    { question: 'How do I create a personal access token?', section: 'personal-access-tokens' },
    { question: 'How do I manage my workspace?', section: 'workspaces' },
    { question: 'How do I view my app analytics?', section: 'analytics', anchor: 'creator-dashboard' },
  ],
  '/changelog': [
    { question: 'How do I get started?', section: 'getting-started' },
    { question: 'What is the platform overview?', section: 'getting-started', anchor: 'platform-overview' },
    { question: 'How do I create an app?', section: 'apps', anchor: 'creating-an-app' },
    { question: 'How do I deploy my app?', section: 'deployment' },
  ],
  '/admin': [
    { question: 'How do I view analytics?', section: 'analytics', anchor: 'admin-analytics' },
    { question: 'How do I manage platform settings?', section: 'getting-started' },
    { question: 'How do I create an app?', section: 'apps', anchor: 'creating-an-app' },
    { question: 'How do I view popular apps?', section: 'analytics', anchor: 'popular-apps' },
    { question: 'How do I get started?', section: 'getting-started', anchor: 'first-steps' },
  ],
  '/': [
    { question: 'How do I get started?', section: 'getting-started' },
    { question: 'How do I create my first app?', section: 'apps', anchor: 'creating-an-app' },
    { question: 'How do I use workspaces?', section: 'workspaces' },
    { question: 'How do I deploy an app?', section: 'deployment', anchor: 'dev-preview' },
  ],
}

function resolveQuestions(pathname: string): QuestionLink[] {
  // Check app detail route first (e.g. /apps/my-app)
  if (pathname.startsWith('/apps/') && pathname !== '/apps') {
    return CONTEXTUAL_QUESTIONS['/apps/:detail']
  }
  // Check specific routes by longest prefix match
  const routes = Object.keys(CONTEXTUAL_QUESTIONS)
    .filter((r) => r !== '/' && r !== '/apps/:detail')
    .sort((a, b) => b.length - a.length)
  for (const route of routes) {
    if (pathname === route || pathname.startsWith(route + '/')) {
      return CONTEXTUAL_QUESTIONS[route]
    }
  }
  return CONTEXTUAL_QUESTIONS['/']
}

function highlightText(text: string, term: string) {
  if (!term || term.length < 2) return text
  const regex = new RegExp(`(${term.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')})`, 'gi')
  const parts = text.split(regex)
  return parts.map((part, i) =>
    regex.test(part) ? (
      <mark key={i} className="bg-primary/20 text-foreground rounded px-0.5">
        {part}
      </mark>
    ) : (
      part
    )
  )
}

export function WidgetHomeTab({ onSwitchToHelp, onStartFeedback }: WidgetHomeTabProps) {
  const user = useUser()
  const { pathname } = useLocation()
  const displayName = user.firstName || user.name || 'there'

  const [query, setQuery] = useState('')
  const [results, setResults] = useState<SearchResult[]>([])
  const [loading, setLoading] = useState(false)
  const [isSearching, setIsSearching] = useState(false)
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  const questions = useMemo(() => resolveQuestions(pathname), [pathname])

  const handleInputChange = useCallback((value: string) => {
    setQuery(value)

    if (timerRef.current) {
      clearTimeout(timerRef.current)
    }

    if (value.length < 2) {
      setResults([])
      setIsSearching(false)
      return
    }

    setIsSearching(true)
    setLoading(true)

    timerRef.current = setTimeout(async () => {
      try {
        let guideResults: GuideSearchResult[] = []
        try {
          guideResults = await searchGuide(value)
        } catch {
          // Guide search may fail silently
        }

        const lowerQuery = value.toLowerCase()
        const tsResults = troubleshootingItems.filter(
          (item) =>
            item.problem.toLowerCase().includes(lowerQuery) ||
            item.cause.toLowerCase().includes(lowerQuery) ||
            item.tags.some((t) => t.toLowerCase().includes(lowerQuery))
        )

        const merged: SearchResult[] = [
          ...guideResults.map((r) => ({
            type: 'guide' as const,
            slug: r.slug,
            title: r.title,
            excerpt: r.excerpt,
          })),
          ...tsResults.map((item) => ({
            type: 'troubleshooting' as const,
            id: item.id,
            title: item.problem,
            excerpt: item.cause,
          })),
        ]

        setResults(merged)
      } catch {
        setResults([])
      } finally {
        setLoading(false)
      }
    }, 300)
  }, [])

  const handleResultClick = useCallback(
    (result: SearchResult) => {
      if (result.type === 'guide') {
        onSwitchToHelp(result.slug)
      } else {
        onSwitchToHelp()
      }
    },
    [onSwitchToHelp]
  )

  const handleQuestionClick = useCallback(
    (link: QuestionLink) => {
      onSwitchToHelp(link.section, link.anchor)
    },
    [onSwitchToHelp]
  )

  return (
    <div className="flex flex-col flex-1 overflow-y-auto">
      {/* Greeting header with modern gradient + abstract illustration */}
      <div className="relative bg-gradient-to-br from-blue-600 via-blue-500 to-indigo-500 px-6 pt-8 pb-6 text-white overflow-hidden">
        {/* Abstract decorative shapes */}
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute -top-6 -right-6 w-32 h-32 rounded-full bg-white/10" />
          <div className="absolute top-8 -right-3 w-20 h-20 rounded-full bg-white/5" />
          <div className="absolute -bottom-4 -left-4 w-24 h-24 rounded-full bg-white/5" />
          <div className="absolute bottom-2 right-12 w-10 h-10 rounded-full bg-white/10" />
          {/* Wave decoration at bottom */}
          <svg className="absolute bottom-0 left-0 right-0 w-full" viewBox="0 0 420 30" fill="none" preserveAspectRatio="none" style={{ height: '20px' }}>
            <path d="M0 30 C70 10, 140 25, 210 15 S350 5, 420 20 L420 30 Z" fill="rgba(255,255,255,0.05)" />
          </svg>
        </div>
        <div className="relative">
          <h2 className="text-xl font-bold">
            Hi {displayName} <span role="img" aria-label="wave">&#x1F44B;</span>
          </h2>
          <p className="text-sm mt-1 text-blue-100">How can we help?</p>
        </div>
      </div>

      {/* Action buttons */}
      <div className="px-5 pt-4 pb-2 flex gap-3">
        <button
          onClick={() => onSwitchToHelp()}
          className="flex-1 flex items-center gap-2.5 px-3 py-2.5 bg-card border border-border rounded-xl hover:bg-secondary/50 transition-colors group shadow-sm"
        >
          <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
            <BookOpen className="w-4 h-4 text-primary" />
          </div>
          <span className="text-sm font-medium text-foreground">User Guide</span>
        </button>
        <button
          onClick={onStartFeedback}
          className="flex-1 flex items-center gap-2.5 px-3 py-2.5 bg-card border border-border rounded-xl hover:bg-secondary/50 transition-colors group shadow-sm"
        >
          <div className="w-8 h-8 rounded-full bg-amber-500/10 flex items-center justify-center flex-shrink-0">
            <Lightbulb className="w-4 h-4 text-amber-500" />
          </div>
          <span className="text-sm font-medium text-foreground">Send Feedback</span>
        </button>
      </div>

      {/* #team-claude help text */}
      <div className="px-5 pb-3">
        <p className="text-xs text-muted-foreground">
          For help, you can also reach out to us in{' '}
          <a
            href={SLACK_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="text-primary font-medium hover:underline"
          >
            #team-claude
          </a>
        </p>
      </div>

      {/* Search bar */}
      <div className="px-5 pb-3">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <input
            type="text"
            value={query}
            onChange={(e) => handleInputChange(e.target.value)}
            placeholder="Search for help..."
            className="w-full pl-9 pr-3 py-2.5 text-sm bg-secondary/50 border border-border rounded-lg placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:border-transparent"
          />
          {loading && (
            <div className="absolute right-3 top-1/2 -translate-y-1/2">
              <Loader2 className="w-4 h-4 animate-spin text-muted-foreground" />
            </div>
          )}
        </div>
      </div>

      {/* Divider */}
      <div className="border-t border-border mt-1" />

      {isSearching ? (
        /* Search results */
        <div className="flex-1 overflow-y-auto">
          {!loading && results.length === 0 && (
            <div className="px-5 py-8 text-center">
              <p className="text-sm text-muted-foreground">No results found</p>
            </div>
          )}
          {results.map((result, i) => (
            <button
              key={`${result.type}-${result.type === 'guide' ? result.slug : result.id}-${i}`}
              onClick={() => handleResultClick(result)}
              className="w-full text-left px-5 py-3 hover:bg-secondary/50 transition-colors border-b border-border last:border-0"
            >
              <div className="flex items-center gap-2 mb-0.5">
                <span
                  className={`text-[10px] font-semibold uppercase tracking-wider px-1.5 py-0.5 rounded ${
                    result.type === 'guide'
                      ? 'bg-blue-500/10 text-blue-500'
                      : 'bg-orange-500/10 text-orange-500'
                  }`}
                >
                  {result.type === 'guide' ? 'Guide' : 'Troubleshooting'}
                </span>
                <span className="text-sm font-medium text-foreground truncate">
                  {result.title}
                </span>
              </div>
              <p className="text-xs text-muted-foreground line-clamp-2 pl-0.5">
                {highlightText(result.excerpt, query)}
              </p>
            </button>
          ))}
        </div>
      ) : (
        /* Context-aware question links */
        <div>
          {questions.map((link) => (
            <button
              key={link.question}
              onClick={() => handleQuestionClick(link)}
              className="w-full flex items-center gap-3 px-5 py-3 hover:bg-secondary/50 transition-colors text-left group min-h-[44px]"
            >
              <HelpCircle className="w-4 h-4 text-primary flex-shrink-0" />
              <span className="text-sm text-foreground flex-1">{link.question}</span>
              <ChevronRight className="w-4 h-4 text-muted-foreground flex-shrink-0" />
            </button>
          ))}
        </div>
      )}
    </div>
  )
}
